import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Location } from '@angular/common';
import { GlobalService } from '../services/global.service';
import { EventSearchService } from './event-search.service';
import { formatApiDate } from '../services/api-date';
import { AuthService } from 'src/app/core/account/auth.service';
import { SelectServiceService } from '../services/select-service.service';
import { ServiceGroup } from '../models/service-group.model';
import { ServiceVenue } from '../models/service-venue.model';
import { MemberSearch } from '../member-search/member-search.model';

@Component({
    selector: 'app-event-search',
    templateUrl: './event-search.component.html',
    styleUrls: ['./event-search.component.scss'],
    standalone: false
})
export class EventSearchComponent implements OnInit, OnChanges  {
  @Output() EventSearchLoaded = new EventEmitter();  
  @Input() AutoMode: boolean = false;
  isLoading: boolean = true;
  loadError = false;
  private initialized = false;
  private loading = false;
  eventGroupId: number = 0;
  eventVenueId: number = 0;
  venue: string = "";
  myStartDate: Date; 
  groupArr: ServiceGroup[] = [];
  venueArr: ServiceVenue[] = [];
    
  constructor(
    public globalService: GlobalService,
    private _location: Location,
    public _eventSearch: EventSearchService, 
    private authService: AuthService,
    private selectService: SelectServiceService
  ) {} 

  async ngOnInit() {
    this.initialized = true;
    await this.loadEvents(true);
  }

  async ngOnChanges(changes: SimpleChanges) {
    // Angular calls OnChanges before OnInit. Do not race the initial load.
    if (this.initialized && changes?.AutoMode && this.globalService.autoMode) {
      this.globalService.eventDate = new Date(new Date().toDateString());
      this.myStartDate = this.globalService.eventDate;
      this.globalService.eventService = this.eventGroupId = 0;
      await this.loadEvents(false);
    }
  }

  async retryLoad() { await this.loadEvents(true); }

  private async bounded<T>(work: Promise<T>): Promise<T> {
    let timer: ReturnType<typeof setTimeout>;
    try {
      return await Promise.race([work, new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('Event initialization timed out')), 20000);
      })]);
    } finally { clearTimeout(timer); }
  }

  private async loadEvents(initialize: boolean) {
    if (this.loading) return;
    this.loading = this.isLoading = true;
    this.loadError = false;
    try {
      if (initialize) {
        await this.bounded(this.authService.autoLogin(1));
        this.myStartDate = this.globalService.eventDate || new Date(new Date().toDateString());
        this.globalService.eventDate = this.myStartDate;
        this.eventGroupId = this.globalService.eventService || 0;
        this.eventVenueId = this.globalService.eventLocation || 0;
        const [groups, venues] = await this.bounded(Promise.all([
          this.selectService.getActiveGroups(), this.selectService.getActiveVenues()
        ]));
        this.groupArr = Array.isArray(groups) ? groups : [];
        this.venueArr = Array.isArray(venues) ? venues : [];
        this.venue = this.venueArr.find(a => a.id === this.eventVenueId)?.name || '';
      }
      await this._eventSearch.getEventsByDate(formatApiDate(this.myStartDate), this.eventGroupId, this.venue);
      this.initializeArr();
    } catch {
      this._eventSearch.eventArr = [];
      this.loadError = true;
    } finally {
      this.loading = this.isLoading = false;
      // Release the parent's full-page spinner on failure as well as success.
      this.EventSearchLoaded.emit();
    }
  }

  goHome() {
    this.globalService.clearCurCache = false;
    // this.globalService.isBack = true;
    this.globalService.eventDate = null;
    this.globalService.eventService = 0;
    this.globalService.eventLocation = 0;
    this._location.back();
  } 
  
  initializeArr()
  {
    this._eventSearch.eventArr.forEach(a => 
      { 
        a['user'] = new MemberSearch();
        a['user']['expandFunctions'] = false;
    }
    );
  }
 
  async DateChange(event) {
    if (this.loading || !event.value) return;
    this.myStartDate = event.value;
    this.globalService.eventDate = event.value;
    await this.loadEvents(false);
  }

  async onGroupChanged(id) {
    if (this.loading) return;
    this.eventGroupId = Number.parseInt(id, 10) || 0;
    this.globalService.eventService = this.eventGroupId;
    await this.loadEvents(false);
  }

  async onVenueChanged(id) {
    if (this.loading) return;
    this.eventVenueId = Number.parseInt(id, 10) || 0;
    this.venue = this.venueArr.find(a => a.id === this.eventVenueId)?.name || '';
    this.globalService.eventLocation = this.eventVenueId;
    await this.loadEvents(false);
  }
}
